import type { ApprovedWarrantyCta } from '@/lib/warranty/model'

/**
 * BreakdownMomentCTA — the call-to-action slot that sits right after the
 * result on the repair-intent tools (the repair-cost estimator, the
 * repair-vs-replace calculator, the power-outage center). FRIDGE-1042.
 *
 * It renders NOTHING until the warranty program registry holds an approved
 * program with a tracked destination — the server page reads the registry
 * (lib/warranty/programs.ts → getApprovedWarrantyCtas) and passes the result
 * down as a plain, serializable prop. An empty array is the state of the
 * world until FRIDGE-713 lands an approval, and an empty array renders null:
 * no heading, no placeholder, no dead link.
 *
 * Every link here is a tracked network destination, disclosed as "Ad" and
 * marked rel="sponsored nofollow noopener". No other outbound provider link
 * exists on the site, and the test suite fails if one appears.
 *
 * No hooks, no browser APIs: it renders identically inside a client
 * component (the tools) and a server component (the hub).
 */
export function BreakdownMomentCTA({
  programs,
  context,
}: {
  programs: ApprovedWarrantyCta[]
  /** One phrase naming the moment, e.g. "a repair quote in hand". */
  context: string
}) {
  if (!programs || programs.length === 0) return null
  return (
    <section
      aria-labelledby="breakdown-moment-cta"
      data-testid="breakdown-moment-cta"
      className="bg-white py-6 md:py-8"
    >
      <div className="max-w-[1120px] mx-auto px-6 md:px-8">
        <div className="relative rounded-2xl border border-gray-200 bg-white p-6 md:p-8">
          <span className="absolute right-4 top-4 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
            Ad
          </span>
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
            If the fridge just died
          </div>
          <h2
            id="breakdown-moment-cta"
            className="font-black text-gray-900 m-0 mb-2"
            style={{ fontSize: 'clamp(20px, 2.4vw, 26px)', letterSpacing: '-0.02em' }}
          >
            Price a home warranty while you have {context}
          </h2>
          <p className="text-[14px] text-gray-600 leading-[1.6] mb-5 max-w-[720px]">
            A home warranty covers the next failure, not this one — pre-existing conditions are excluded on
            every plan we have read. Get the quote now so the next repair bill is a service fee instead of
            the full invoice. Disclosed paid placement: Fridge.com may earn a fee if you request a quote.
          </p>
          <div className="flex flex-wrap gap-3">
            {programs.map((p) => (
              <a
                key={p.provider_slug}
                href={p.href}
                target="_blank"
                rel="sponsored nofollow noopener"
                data-warranty-program={p.provider_slug}
                className="inline-flex items-center justify-center rounded-lg bg-[#0469f0] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0053c4] transition-colors"
              >
                Get a quote from {p.provider_name}
                {p.plan_name ? ` · ${p.plan_name}` : ''} &rarr;
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
